import React, { useEffect, useState, useMemo } from 'react';
import Footer from './common/home/Footer';
import TopBar from './common/home/TopBar';
import { 
  Box, 
  Button,
  Card,
  CardContent,
  Container,
  Fade,
  Typography,
  Zoom
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Dashboard,
  Email,
  Refresh,
  ArrowForward
} from '@mui/icons-material';
import { homeBackground } from './common/ColorCodes';
import './common/Styles.css'
import './common/PaymentStatusStyles.css'
import { Link, useNavigate } from 'react-router-dom';
import { CONST_ROUTE_PATH } from '../route/RouteConstants';
import { CONST_BUSINESS_MAIL, setScopedItem, STORAGE_KEYS } from './common/Helper';

/**
 * Modern Payment Status Component
 * Displays payment success/failure with enhanced UX and optimized logic
 * @returns 
 */
export const PaymentStatus = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);

  // Optimized payment status detection with memoization
  const paymentStatus = useMemo(() => {
    const pathname = window.location.pathname.toLowerCase();
    const searchParams = new URLSearchParams(window.location.search);
    
    // Multiple ways to detect success/failure for better reliability
    const isSuccessPath = pathname.includes('thank') || pathname.includes('success');
    const isFailurePath = pathname.includes('failed') || pathname.includes('error');
    const statusParam = searchParams.get('status');
    
    if (statusParam) {
      return statusParam === 'success' || statusParam === 'completed';
    }
    
    return isSuccessPath ? true : isFailurePath ? false : null;
  }, []);

  // Enhanced loading and title management
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 800); // Smooth loading transition

    document.title = paymentStatus === true 
      ? 'Payment Successful - FinOps Platform'
      : paymentStatus === false 
      ? 'Payment Failed - FinOps Platform'
      : 'Processing Payment - FinOps Platform';

    return () => clearTimeout(timer);
  }, [paymentStatus]);

  // Enhanced action handlers
  const handleDashboardNavigation = () => {
    setScopedItem(STORAGE_KEYS.CURRENT_ROUTE, CONST_ROUTE_PATH.DASHBOARD)
    navigate(CONST_ROUTE_PATH.DASHBOARD);
  };

  const handleRetryPayment = () => {
    navigate(CONST_ROUTE_PATH.PRICING);
  };

  const handleContactSupport = () => {
    window.open(`mailto:${CONST_BUSINESS_MAIL}?subject=Payment Support Request`, '_blank');
  };

  // Loading state with modern design
  if (isLoading || paymentStatus === null) {
    return (
      <div className="payment-status-page" style={{ backgroundColor: homeBackground }}>
        <TopBar />
        <Container maxWidth="sm" className="payment-status-container">
          <Card className="payment-loading-card">
            <div className="payment-loading-spinner" />
            <Typography variant="h6" color="text.secondary">
              Processing your payment...
            </Typography>
          </Card>
        </Container>
        <Footer />
      </div>
    );
  }

  return (
    <div className="payment-status-page" style={{ backgroundColor: homeBackground }}>
      <TopBar />
      
      <Container maxWidth="md" className="payment-status-container">
        <div className="fade-in">
          <Card className={`payment-status-card ${paymentStatus ? 'success' : 'failure'}`}>
            <CardContent sx={{ p: 0 }}>
              {/* Header Section */}
              <div className={`payment-status-header ${paymentStatus ? 'success' : 'failure'}`}>
                <div className="zoom-in">
                  {paymentStatus ? (
                    <CheckCircle className="payment-status-icon" />
                  ) : (
                    <Cancel className="payment-status-icon" />
                  )}
                  <Typography variant="h4" className="payment-status-title">
                    {paymentStatus ? 'Payment Successful!' : 'Payment Failed'}
                  </Typography>
                  <Typography variant="body1" className="payment-status-subtitle">
                    {paymentStatus 
                      ? 'Your subscription has been activated successfully'
                      : 'We encountered an issue processing your payment'
                    }
                  </Typography>
                </div>
              </div>

              {/* Content Section */}
              <div className="payment-status-content">
                {paymentStatus ? (
                  // Success Content
                  <div>
                    <Typography variant="h6" className="payment-status-welcome">
                      🎉 Welcome to FinOps Platform!
                    </Typography>
                    <Typography variant="body1" color="text.secondary" className="payment-status-description">
                      Your subscription is now active. You can access all premium features including advanced analytics, 
                      cost optimization recommendations, and comprehensive reporting.
                    </Typography>
                    
                    <div className="payment-status-actions">
                      <Button
                        variant="contained"
                        size="large"
                        onClick={handleDashboardNavigation}
                        startIcon={<Dashboard />}
                        endIcon={<ArrowForward />}
                        className="payment-btn payment-btn-primary"
                      >
                        Go to Dashboard
                      </Button>
                    </div>
                  </div>
                ) : (
                  // Failure Content
                  <div>
                    <Typography variant="h6" className="payment-status-help">
                      Don't worry, we're here to help!
                    </Typography>
                    <Typography variant="body1" color="text.secondary" className="payment-status-description">
                      Payment processing can sometimes encounter temporary issues. You can try again or contact our 
                      support team for immediate assistance.
                    </Typography>
                    
                    <div className="payment-status-actions">
                      <Button
                        variant="contained"
                        size="large"
                        onClick={handleRetryPayment}
                        startIcon={<Refresh />}
                        className="payment-btn payment-btn-retry"
                      >
                        Try Again
                      </Button>
                      
                      <Button
                        variant="outlined"
                        size="large"
                        onClick={handleContactSupport}
                        startIcon={<Email />}
                        className="payment-btn payment-btn-support"
                      >
                        Contact Support
                      </Button>
                    </div>
                    
                    <Typography variant="caption" color="text.secondary" className="payment-support-text">
                      Need immediate help? Email us at{' '}
                      <Link 
                        href={`mailto:${CONST_BUSINESS_MAIL}`} 
                        className="payment-support-link"
                      >
                        {CONST_BUSINESS_MAIL}
                      </Link>
                    </Typography>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </Container>
      
      <Footer />
    </div>
  );
};
